"use client";

import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { CardContent } from "./ui/card";
import { useToast } from "../hooks/use-toast";
import { Loader2 } from "lucide-react";
import info from "../assets/icons/information-circle-blue.svg";
import { createWalletAddress } from "../services/digitalWalletService";
import { CurrencySelector } from "../components/currencyDropdown2";
import SimpleSelect from "../components/SimpleSelect";
import angle_down from "../assets/icons/angle-down.svg";

const AddContact = ({
  selectedAsset,
  setSelectedAsset,
  onContactAdded,
  selectedNetwork,
  setSelectedNetwork,
  hotActionAsset,
  initialAddress,
}) => {
  const [contactType, setContactType] = useState("Individual");
  const [newName, setNewName] = useState("");
  const [newLastname, setNewLastname] = useState("");
  const [newAddress, setNewAddress] = useState(initialAddress ?? "");

  useEffect(() => {
    if (initialAddress != null && String(initialAddress).trim() !== "") {
      setNewAddress(String(initialAddress).trim());
    }
  }, [initialAddress]);
  const [addName, setAddName] = useState("");
  const [walletType, setWalletType] = useState("Self Hosted");
  const [serviceProvider, setServiceProvider] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    newName: "",
    newAddress: "",
    selectedAsset: "",
    selectedNetwork: "",
    serviceProvider: "",
  });
  const { toast } = useToast();

  const handleAssetSelect = (asset) => {
    setSelectedAsset(asset);
    setErrors((prev) => ({ ...prev, selectedAsset: "" }));
  };

  const handleNetworkSelect = (network) => {
    setSelectedNetwork(network);
    setErrors((prev) => ({ ...prev, selectedNetwork: "" }));
  };

  const validateForm = () => {
    const newErrors = {
      newName: "",
      newAddress: "",
      selectedAsset: "",
      selectedNetwork: "",
      serviceProvider: "",
    };
    let isValid = true;

    if (!newName || newName.trim() === "") {
      newErrors.newName = "Name is required.";
      isValid = false;
    }

    if (!newAddress || newAddress.trim() === "") {
      newErrors.newAddress = "Wallet address is required.";
      isValid = false;
    }

    if (!selectedAsset) {
      newErrors.selectedAsset = "Please select an asset.";
      isValid = false;
    }

    if (walletType === "Exchange" && !serviceProvider) {
      newErrors.serviceProvider = "Please select a service provider.";
      isValid = false;
    }

    setErrors(newErrors);
    return { isValid, newErrors };
  };

  const handleAddContact = async () => {
    try {
      setLoading(true);
      const { isValid } = validateForm();

      if (!isValid) {
        setLoading(false);
        return;
      }

      const newestAddress = {
        firstName: newName,
        lastName: newLastname,
        addressName: addName,
        walletAddress: newAddress,
        asset: selectedAsset?.assetId,
        assetName: selectedAsset?.assetName,
        chain: selectedNetwork?._id,
        chainName: selectedNetwork?.chain,
        category: contactType,
        walletType: walletType,
        provider: serviceProvider,
      };

      const address = await createWalletAddress(newestAddress);
      if (address) {
        toast({
          description: "Address Added",
          variant: "eighty",
        });

        const newWallet = {
          firstName: newName,
          lastName: newLastname,
          addressName: addName,
          walletAddress: newAddress,
          asset: selectedAsset?.assetId,
          assetName: selectedAsset?.assetName,
          chain: selectedNetwork?._id,
          chainName: selectedNetwork?.chain,
          walletType: walletType,
          provider: serviceProvider,
          category: contactType,
        };

        // Reset form
        setNewName("");
        setNewLastname("");
        setNewAddress("");
        setAddName("");
        setWalletType("Self Hosted");
        setServiceProvider("");
        setErrors({
          newName: "",
          newAddress: "",
          selectedAsset: "",
          selectedNetwork: "",
          serviceProvider: "",
        });
        if (onContactAdded) {
          onContactAdded(newWallet);
        }
      }
    } catch (error) {
      toast({
        description: `${error.message || error}`,
        variant: "eighty",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CardContent>
      <h2 className="text-[18px] lg:text-[20px] font-semibold text-[#6D6584] mb-4 lg:mb-2">
        Add a new contact
      </h2>

      <div className="flex mb-6 items-center justify-center text-center lg:text-left gap-3 lg:gap-4 bg-[#EFF1FC] font-semibold rounded-2xl p-3 lg:p-4 text-[#6375E6] text-[13px] lg:text-[14px]">
        <img
          src={info}
          alt="info"
          width={16}
          height={16}
          className="flex-shrink-0"
        />
        This information is required for compliance with global Travel Rule
        regulations.
      </div>

      <div className="mb-6 lg:mb-8">
        <div className="grid grid-cols-2 rounded-full h-full bg-[#EBE9EF4D]">
          <button
            onClick={() => setContactType("Individual")}
            className={`py-3 lg:py-4 m-1 font-medium rounded-full transition-colors text-sm lg:text-base ${
              contactType === "Individual"
                ? "bg-[#33125F] text-white"
                : "text-gray-600"
            }`}
          >
            Individual
          </button>
          <button
            onClick={() => setContactType("Corporate")}
            className={`py-3 lg:py-4 m-1 font-medium rounded-full transition-colors text-sm lg:text-base ${
              contactType === "Corporate"
                ? "bg-[#33125F] text-white"
                : "text-gray-600"
            }`}
          >
            Corporate
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row justify-between w-full gap-4 lg:gap-4 mb-6">
        <div className="flex-1 w-full">
          <CurrencySelector
            onSelectNet={handleNetworkSelect}
            onSelect={handleAssetSelect}
            setSelectedAsset={setSelectedAsset}
            selectedAsset={selectedAsset}
            selectedNetwork={selectedNetwork}
            isSend={true}
            hotActionAsset={hotActionAsset}
            isEightyeightPay={false}
            showAvailableBalance={false}
          />
          {errors.selectedAsset && (
            <p className="mt-2 text-sm text-rose-500">{errors.selectedAsset}</p>
          )}
          {errors.selectedNetwork && (
            <p className="mt-2 text-sm text-rose-500">
              {errors.selectedNetwork}
            </p>
          )}
        </div>
        <div className="flex-1 w-full">
          <label className="block text-[14px] text-[#6D6584] mb-2">
            Wallet address
          </label>
          <input
            type="text"
            value={newAddress}
            onChange={(e) => {
              setNewAddress(e.target.value);
              setErrors({ ...errors, newAddress: "" });
            }}
            className={`w-full border rounded-lg px-4 py-3 h-[70px] lg:h-[88px] transition-colors ${
              errors.newAddress
                ? "border-rose-500 border-2"
                : "border-[#CECBD7]"
            }`}
            placeholder="0x3A5dCBF7fB8b21A1D2E6e3E4dA8f2iC7eF789F5"
          />
          {errors.newAddress && (
            <p className="mt-2 text-sm text-rose-500">{errors.newAddress}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-4 mb-6 lg:mb-8">
        <div>
          <label className="block text-[14px] text-[#6D6584] mb-2">Name</label>
          <input
            type="text"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setErrors({ ...errors, newName: "" });
            }}
            className={`w-full border rounded-lg px-4 py-3 h-[70px] lg:h-[66px] transition-colors ${
              errors.newName ? "border-rose-500 border-2" : "border-[#CECBD7]"
            }`}
            placeholder="Paul"
          />
          {errors.newName && (
            <p className="mt-2 text-sm text-rose-500">{errors.newName}</p>
          )}
        </div>
        <div>
          <label className="block text-[14px] text-[#6D6584] mb-2">
            Surname
          </label>
          <input
            type="text"
            value={newLastname}
            onChange={(e) => setNewLastname(e.target.value)}
            className="w-full border rounded-lg px-4 py-3 h-[70px] lg:h-[66px] border-[#CECBD7]"
            placeholder="Moodley"
          />
        </div>
      </div>

      <div className="mb-6 lg:mb-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
        <label className="inline-block text-[14px] text-[#6D6584] lg:w-auto">
          Wallet type
        </label>
        <div className="inline-flex flex-wrap gap-4 lg:gap-6">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={walletType === "80eight"}
              onChange={() => setWalletType("80eight")}
              className="w-[28px] lg:w-[32px] h-[28px] lg:h-[32px]"
            />
            <span className="text-gray-700 text-sm lg:text-base">80eight</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={walletType === "Self Hosted"}
              onChange={() => setWalletType("Self Hosted")}
              className="w-[28px] lg:w-[32px] h-[28px] lg:h-[32px]"
            />
            <span className="text-gray-700 text-sm lg:text-base">
              Self-hosted
            </span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={walletType === "Exchange"}
              onChange={() => setWalletType("Exchange")}
              className="w-[28px] lg:w-[32px] h-[28px] lg:h-[32px]"
            />
            <span className="text-gray-700 text-sm lg:text-base">Exchange</span>
          </label>
        </div>
      </div>

      {walletType === "Exchange" && (
        <div className="mb-6">
          <SimpleSelect
            label="Select service provider"
            placeholder="Select platform"
            options={[
              "AltCoinTrader",
              "Binance",
              "BingX",
              "Bitget",
              "BloFin",
              "Bybit",
              "Coinbase",
              "Coinmama",
              "Crypto.com",
              "eToro",
              "Gate.io",
              "Gemini",
              "Kraken",
              "KuCoin",
              "Luno",
              "MEXC",
              "OKX",
              "PrimeXBT",
              "VALR",
              "Yellow Card",
              "Bitpanda",
              "Coinpass",
              "Kriptomat",
            ]}
            icon={
              <img
                src={angle_down}
                className="w-[16px] h-[16px]"
                alt="dropdown"
              />
            }
            name="title"
            value={serviceProvider}
            onChange={(e) => {
              setServiceProvider(e.target.value);
              setErrors({ ...errors, serviceProvider: "" });
            }}
            className="w-full"
          />
          {errors.serviceProvider && (
            <p className="mt-2 text-sm text-rose-500">
              {errors.serviceProvider}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 pt-4 lg:pt-6">
        <Button
          variant="outline"
          className="bg-transparent border-[1px] border-[#EBE9EF] text-[#6D6584] px-6 py-4 rounded-full font-semibold hover:opacity-90 hover:bg-transparent transition-opacity w-full lg:w-[49%] h-[48px]"
          onClick={() => {
            const { isValid } = validateForm();
            if (!isValid) return;
            const oneOffWallet = {
              firstName: newName,
              lastName: newLastname,
              addressName: addName,
              walletAddress: newAddress,
              asset: selectedAsset?.assetId,
              assetName: selectedAsset?.assetName,
              chain: selectedNetwork?._id,
              chainName: selectedNetwork?.chain,
              walletType: walletType,
              provider: serviceProvider,
              category: contactType,
            };
            onContactAdded?.(oneOffWallet);
          }}
        >
          One-off payment
        </Button>
        <Button
          onClick={handleAddContact}
          disabled={loading}
          className="bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white px-6 py-4 rounded-full font-semibold hover:opacity-90 transition-opacity w-full lg:w-[49%] h-[48px] disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading && <Loader2 className="animate-spin w-4 h-4 mr-2" />}
          {loading ? "Saving..." : "Save contact + Continue"}
        </Button>
      </div>
    </CardContent>
  );
};

export default AddContact;
